import { LoadingContent } from "@/components/LoadingContent";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Alert, Anchor, Box, Button, Center, Container, Group, Image, Stack, Text, Title } from "@mantine/core";
import QRCode from "react-qr-code";
import { AppLogo } from "@/components/AppLogo";
import { appLinksOptions } from "@/features/app-info/queries";
import { LinkIcon } from "@/components/LinkIcon";

function StoreQrCode({ url, platform }: { url: string; platform: "iOS" | "Android" }) {
  const label = `Scan to get TripBears for ${platform}`;
  return <Box p={16} mt="sm">
    {/* Omitting bgColor defaults to white; SVG transparent keeps the page visible. */}
    <QRCode value={url} size={84} level="L" bgColor="transparent" fgColor="var(--mantine-color-bright)" role="img" aria-label={label} title={label} style={{ display: "block" }} />
  </Box>;
}

function AppsPage() {
  const stores = useQuery(appLinksOptions());

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg" align="center">
        <AppLogo size={88} alt="" />
        <Title order={1} ta="center">Apps and Integrations</Title>
        <Text ta="center">Get the mobile apps and connect TripBears to your favourite tools.</Text>
        {stores.isError ? (
          <Alert color="red" title={stores.data ? "Couldn't refresh apps and integration links" : "Couldn't load apps and integration links"} w="100%">
            <Stack gap="sm">
              <Text size="sm">Please try again.</Text>
              <Button variant="light" color="red" loading={stores.isFetching} onClick={() => void stores.refetch()}>Retry</Button>
            </Stack>
          </Alert>
        ) : null}
        <>
          <Title order={2} ta="center">Apps</Title>
          <Text ta="center">Download TripBears for iOS or Android.</Text>
          <Group justify="center" align="flex-start" gap="md" aria-label="Download TripBears">
            <Stack align="center" gap={4}>
              <Center h={60}>
                {stores.data?.ios ? (
                  <Anchor href={stores.data.ios} target="_blank" rel="noopener noreferrer" aria-label="Download TripBears on the App Store (opens in a new tab)" style={{ display: "inline-flex" }}>
                    <Image src="/store-badges/app-store.svg" alt="Download on the App Store" w={120} h={40} />
                  </Anchor>
                ) : <Image src="/store-badges/app-store.svg" alt={stores.isPending ? "Download on the App Store (checking availability)" : "App Store unavailable"} w={120} h={40} style={{ filter: "grayscale(1) opacity(0.45)" }} />}
              </Center>
              {stores.data && !stores.data.ios ? <Text size="xs" c="dimmed">Unavailable</Text> : null}
              {stores.isPending ? <Box w={116}><LoadingContent label="Loading iOS app link…" variant="rows" rows={1} height={116} /></Box> : null}
              {stores.data?.ios ? <StoreQrCode url={stores.data.ios} platform="iOS" /> : null}
            </Stack>
            <Stack align="center" gap={4}>
              <Center h={60}>
                {stores.data?.android ? (
                  <Anchor href={stores.data.android} target="_blank" rel="noopener noreferrer" aria-label="Get TripBears on Google Play (opens in a new tab)" style={{ display: "inline-flex" }}>
                    <Image src="/store-badges/google-play.png" alt="Get it on Google Play" w={155} h={60} />
                  </Anchor>
                ) : <Image src="/store-badges/google-play.png" alt={stores.isPending ? "Get it on Google Play (checking availability)" : "Google Play unavailable"} w={155} h={60} style={{ filter: "grayscale(1) opacity(0.45)" }} />}
              </Center>
              {stores.data && !stores.data.android ? <Text size="xs" c="dimmed">Unavailable</Text> : null}
              {stores.isPending ? <Box w={116}><LoadingContent label="Loading Android app link…" variant="rows" rows={1} height={116} /></Box> : null}
              {stores.data?.android ? <StoreQrCode url={stores.data.android} platform="Android" /> : null}
            </Stack>
          </Group>
          <Stack component="section" w="100%" gap="sm" aria-labelledby="integration-links-heading">
            <Title id="integration-links-heading" order={2} ta="center">Plugins, Skills and MCP</Title>
            {stores.isPending ? <LoadingContent label="Loading integration links…" variant="rows" /> : stores.data?.integrations.length ? stores.data.integrations.map((link, index) => (
              <Button key={`${link.id}:${index}`} component="a" href={link.url} target="_blank" rel="noopener noreferrer"
                aria-label={`${link.name} (opens in a new tab)`} variant="light" fullWidth mih={44} h="auto" py="sm"
                leftSection={<LinkIcon slug={link.icon} />} styles={{ label: { whiteSpace: "normal", textAlign: "left" } }}>
                {link.name}
              </Button>
            )) : stores.data ? <Text size="sm" c="dimmed" ta="center">No plugins, skills or MCP links are available yet.</Text> : null}
            <Text size="sm" ta="center"><Anchor renderRoot={(props) => <Link {...props} to="/mcp" />}>Learn how to connect your AI assistant</Anchor></Text>
          </Stack>
        </>
        <Text size="sm" c="dimmed" ta="center">
          Visit <Anchor href="https://tripbears.com">TripBears</Anchor> for app availability. The mobile app uses its own sign-in session.
        </Text>
      </Stack>
    </Container>
  );
}

export const Route = createFileRoute("/mobile-apps")({ component: AppsPage });
